import React, { useState, useEffect } from 'react';
import { Typography, Card, Space, Button, Breadcrumb, Divider, Spin } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { scheduleAPI, tokenManager } from '../services/api';
import { message } from '../utils/message';

const { Title, Text } = Typography;

interface ScheduleContent {
  id?: number;
  contentItemId: number;
  displayOrder: number;
  duration: string;
  contentItem?: {
    id: number;
    contentType: string;
    contentTitle: string;
    description?: string;
    linkUrl?: string;
  };
}

interface Schedule {
  id: number;
  scheduleType: string;
  timeSlotName: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  contents: ScheduleContent[];
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

const ScheduleDetail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('id');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [contents, setContents] = useState<ScheduleContent[]>([]);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  // 사용자 권한 확인
  const user = tokenManager.getUser();
  const isSuperAdmin = user?.role === 'super_admin';

  // 스케줄 데이터 로드
  useEffect(() => {
    const loadSchedule = async () => {
      if (!scheduleId) {
        message.error('스케줄 ID가 없습니다.');
        navigate('/schedule-management');
        return;
      }

      try {
        setLoading(true);
        const response = await scheduleAPI.getSchedule(Number(scheduleId));
        setSchedule(response.data);
        setContents(response.data.contents || []);
      } catch (error: any) {
        message.error(error.response?.data?.message || '스케줄을 불러오는데 실패했습니다.');
        navigate('/schedule-management');
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [scheduleId, navigate]);

  // 뒤로가기
  const handleBack = () => {
    navigate('/schedule-management');
  };

  // 자동 저장
  const autoSave = async () => {
    if (!schedule || !isSuperAdmin) return;

    try {
      setSaving(true);
      const updatedContents = contents.map((content, index) => ({
        contentItemId: content.contentItemId,
        displayOrder: index + 1,
        duration: content.duration
      }));

      await scheduleAPI.updateScheduleContents(schedule.id, updatedContents);
      message.success('성공적으로 저장되었습니다.');

      // 데이터 다시 로드
      const response = await scheduleAPI.getSchedule(schedule.id);
      setSchedule(response.data);
      setContents(response.data.contents || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 드래그 앤 드롭 핸들러들
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!isSuperAdmin) return;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!isSuperAdmin) return;
    if (draggedItem !== null && draggedItem !== targetIndex) {
      setDragOverItem(targetIndex);

      // 실시간 순서 변경 미리보기
      const newContents = [...contents];
      const [removed] = newContents.splice(draggedItem, 1);
      newContents.splice(targetIndex, 0, removed);

      setContents(newContents);
      setDraggedItem(targetIndex);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = async () => {
    setDraggedItem(null);
    setDragOverItem(null);

    // 드래그 완료 시 자동 저장
    await autoSave();
  };

  const handleMouseEnter = (index: number) => {
    if (draggedItem === null) {
      setHoveredItem(index);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  // 총 러닝타임 계산
  const calculateTotalDuration = () => {
    const totalSeconds = contents.reduce((sum, content) => {
      const [min, sec] = content.duration.split(':').map(Number);
      return sum + min * 60 + sec;
    }, 0);
    const totalMin = Math.floor(totalSeconds / 60);
    const totalSec = totalSeconds % 60;
    return `러닝타임 : ${String(totalMin).padStart(2, '0')}분 ${String(totalSec).padStart(2, '0')}초`;
  };

  if (loading || !schedule) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          {
            title: '스케쥴 관리',
            href: '/schedule-management'
          },
          { title: '스케쥴 상세' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 0 32px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
            스케쥴 상세
          </Title>
        </div>
        {!isSuperAdmin && (
          <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>
            스케쥴 변경 필요 시 HNINE 문의 바랍니다. 02-521-0518
          </Text>
        )}
      </div>

      {/* 기본 정보 섹션 */}
      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>기본 정보</Text>}
        style={{
          marginBottom: '24px',
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '60px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>구분</Text>
              <Text style={{ color: '#ffffff', fontSize: '14px' }}>{schedule.scheduleType}</Text>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '60px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>노출 시간</Text>
              <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                {schedule.startTime.substring(0, 5)} - {schedule.endTime.substring(0, 5)}
              </Text>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '60px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>스케쥴명</Text>
              <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                {schedule.scheduleType} {schedule.timeSlotName}
              </Text>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '60px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>활성화 상태</Text>
              <Text style={{ color: schedule.isActive ? '#52c41a' : '#ff4d4f', fontSize: '14px' }}>
                {schedule.isActive ? '활성화' : '비활성화'}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {/* 시퀀스 정보 섹션 */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>시퀀스 정보</Text>
            {isSuperAdmin && (
              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                드래그하여 순서를 변경할 수 있습니다
              </Text>
            )}
          </div>
        }
        style={{
          marginBottom: '24px',
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0',
        }}>
          {contents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#a6a6a6' }}>
              <Text>콘텐츠가 없습니다.</Text>
            </div>
          ) : (
            contents.map((content, index) => (
              <div
                key={index}
                style={{
                  transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  willChange: 'transform'
                }}
              >
                <div
                  draggable={isSuperAdmin}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 0',
                    backgroundColor: hoveredItem === index ? '#2a2a2a' : 'transparent',
                    marginLeft: '-24px',
                    marginRight: '-24px',
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    cursor: isSuperAdmin ? 'move' : 'default',
                    opacity: draggedItem === index ? 0.6 : 1,
                    transform: draggedItem === index
                      ? 'scale(1.02)'
                      : draggedItem !== null && draggedItem !== index
                        ? 'scale(0.98) translateY(0px)'
                        : 'scale(1)',
                    transition: draggedItem === index
                      ? 'opacity 0.2s ease, transform 0.2s ease, border 0.2s ease'
                      : 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    border: draggedItem === index
                      ? '2px dashed #ff8c00'
                      : dragOverItem === index
                        ? '2px solid #52c41a'
                        : '2px solid transparent',
                    borderRadius: '6px',
                    boxShadow: dragOverItem === index
                      ? '0 4px 8px rgba(82, 196, 26, 0.2)'
                      : 'none',
                    zIndex: draggedItem === index ? 1000 : 1,
                    willChange: 'transform, opacity'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%'
                  }}>
                    {isSuperAdmin && (
                      <HolderOutlined style={{
                        color: draggedItem === index ? '#ff8c00' : '#a6a6a6',
                        fontSize: '16px',
                        marginRight: '8px',
                        cursor: draggedItem === index ? 'grabbing' : 'grab',
                        transition: 'color 0.2s ease'
                      }} />
                    )}
                    <Text style={{
                      color: '#a6a6a6',
                      fontSize: '14px',
                      minWidth: '30px'
                    }}>
                      {String(index + 1).padStart(2, '0')}
                    </Text>
                    {(() => {
                      const contentTitle = content.contentItem?.contentTitle || `ContentItem #${content.contentItemId}`;
                      const linkUrl = content.contentItem?.linkUrl;

                      if (linkUrl) {
                        return (
                          <span
                            onClick={(e) => {
                              // 드래그 중에는 클릭 방지
                              if (draggedItem !== null) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                              }
                              navigate(linkUrl);
                            }}
                            style={{
                              color: '#ffffff',
                              fontSize: '14px',
                              flex: 1,
                              textDecoration: 'none',
                              cursor: draggedItem !== null ? 'move' : 'pointer'
                            }}
                          >
                            {contentTitle}
                          </span>
                        );
                      }

                      return (
                        <Text style={{
                          color: '#ffffff',
                          fontSize: '14px',
                          flex: 1
                        }}>
                          {contentTitle}
                        </Text>
                      );
                    })()}
                    <Text style={{
                      color: '#a6a6a6',
                      fontSize: '14px',
                      minWidth: '60px',
                      textAlign: 'right'
                    }}>
                      {content.duration}
                    </Text>
                  </div>
                </div>
                {index < contents.length - 1 && (
                  <Divider style={{
                    margin: '0',
                    borderColor: '#303030',
                    transition: 'all 0.3s ease',
                    opacity: draggedItem !== null ? 0.7 : 1
                  }} />
                )}
              </div>
            ))
          )}
        </div>

        {contents.length > 0 && (
          <div style={{
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #303030'
          }}>
            <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>
              {calculateTotalDuration()}
            </Text>
          </div>
        )}
      </Card>

      {/* 하단 버튼 */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        marginTop: '32px'
      }}>
        <Button
          size="large"
          onClick={handleBack}
          style={{
            minWidth: '100px',
            background: 'transparent',
            border: '1px solid #303030',
            color: '#ffffff'
          }}
        >
          이전
        </Button>
      </div>
    </Space>
  );
};

export default ScheduleDetail;
